import React, { useState, useMemo } from "react";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import {
  Grid,
  Card,
  CardMedia,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from "@mui/material";
import ProductModal from "./product_modal";
// import { REACT_APP_API_BASE_URL } from "../config";
import { getAuthHeaders } from "../context/AuthContext";
import axios from "axios";
import { getApiUrl } from "../utils/apiHelper";
import { useAuth } from "../context/AuthContext";

export default function SearchResults({ results, method, image_key, file, hasSearched, reported, setReported, client }) {
  const { loginType } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const [sortKey, setSortKey] = useState("combined_score");

  const handleOpen = (sku, rank) => {
    setSelectedItem({ sku: sku, rank: rank, client: client });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 10, results.length));
  };

  const handleSortChange = (event) => {
    setSortKey(event.target.value);
    setVisibleCount(10);
  };

  const sortedResults = useMemo(() => {
    if (["clip_gemini_chroma", "vertex_loftr_chroma"].includes(method)) {
      return [...results].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
    }
    return results;
  }, [results, sortKey, method]);

  const visibleResults = sortedResults.slice(0, visibleCount);

  const renderScore = (img) => {
    switch (method) {
      case "clip_gemini_chroma":
        return (
          <>
            <Score label="Combined" value={img.combined_score} />
            <Score label="Image" value={img.image_score} />
            <Score label="Text" value={img.text_score} />
          </>
        );
      case "vertex_loftr_chroma":
        return (
          <>
            <Score label="Combined" value={img.combined_score} />
            <Score label="Local" value={img.loftr_score} />
            <Score label="Global" value={img.vertex_score} />
          </>
        );
      case "clip_chroma":
      case "vertex_chroma":
      case "vertex_gemini_chroma_1":
        return <Score label="Score" value={img.score} />;
      case "vertex_gemini_chroma_2":
        return (
          <>
            <Score label="Image" value={img.image_score} />
            <Score label="Text" value={img.text_score} />
            <Score label="Combined" value={img.combined_score} />
          </>
        );
      default:
        return null;
    }
  };

  const handleReportNotFound = async (image_key) => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        getApiUrl(loginType, "/report/image/not_found"),
        { image_key },
        { headers }
      );
      setReported(true);
    } catch (error) {
      console.error("Report Not Found error:", error);
      alert("Failed to submit report. See console for details.");
    }
  }

  const ReportNotFoundButton = () => (
    <Tooltip title={reported ? "Already reported" : ""}>
      <Button
        variant="outlined"
        color="error"
        startIcon={<ReportProblemIcon />}
        disabled={reported}
        onClick={() => handleReportNotFound(image_key)}
        sx={{ px: 4, py: 1.2, borderRadius: 2, ml: 2 }}
      >
        
        {!reported? "Not Found": "Already Reported"}
      </Button>
    </Tooltip>
  )

  return (
    <>
      {results.length > 0 && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Search Results
        </Typography>
      )}

      {["clip_gemini_chroma", "vertex_loftr_chroma"].includes(method) && (
        <Box sx={{ mb: 3, maxWidth: 300 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="sort-select-label">Sort By</InputLabel>
            <Select
              labelId="sort-select-label"
              id="sort-select"
              value={sortKey}
              label="Sort By"
              onChange={handleSortChange}
            >
              {method === "clip_gemini_chroma" && (
                <>
                  <MenuItem value="combined_score">Combined Score</MenuItem>
                  <MenuItem value="image_score">Image Score</MenuItem>
                  <MenuItem value="text_score">Text Score</MenuItem>
                </>
              )}
              {method === "vertex_loftr_chroma" && (
                <>
                  <MenuItem value="combined_score">Combined Score</MenuItem>
                  <MenuItem value="loftr_score">Local Feature Score</MenuItem>
                  <MenuItem value="vertex_score">Global Feature Score</MenuItem>
                </>
              )}
            </Select>
          </FormControl>
        </Box>
      )}

      <Grid justifyContent={"center"} container spacing={3}>
        {visibleResults.map((img, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card
              sx={{
                position: "relative",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 3,
                cursor: "pointer",
                transition: "transform 0.2s",
                "&:hover": { transform: "scale(1.02)" },
              }}
              onClick={() => handleOpen(img.sku, idx + 1)}
            >
              {/* Tags in top-right corner */}
              {img.metadata && Array.isArray(img.metadata.tags) && img.metadata.tags.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {img.metadata.tags.filter(tag => tag.toLowerCase().includes("sku_")).map((tag, i) => (
                    <Box
                      key={i}
                      sx={{
                        bgcolor: "primary.main",
                        color: "#fff",
                        px: 1,
                        py: 0.2,
                        borderRadius: 1,
                        fontSize: "0.6rem",
                        fontWeight: 500,
                        boxShadow: 1,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
                </Box>
              )}
              <CardMedia
                component="img"
                height="220"
                image={img.top_match_image_url}
                alt={`Result ${idx + 1}`}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  bgcolor: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  color: "#fff",
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "inline-block",
                    bgcolor: "#fff",
                    color: "#000",
                    px: 1.5,
                    py: 0.5,
                    mb: 1,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    boxShadow: 1,
                  }}
                >
                  SKU: {img.sku}
                  {img.metadata && (
                    (() => {
                      const gRankValue = img.metadata["GOLD_RANK"];
                      const rankValue = img.metadata["RANK"];
                      const isGoldSku = img.sku.startsWith("GD");
                      const hasGRank = gRankValue != null;
                      const hasRank = rankValue != null;

                      if (!hasGRank && !hasRank) return null;

                      const showRank = isGoldSku && hasGRank ? gRankValue : rankValue;
                      const showLabel = isGoldSku && hasGRank ? "#GRank" : "#Rank";



                      return rankValue ? (
                        <span 
                          style={{ 
                            marginLeft: 8, 
                            fontWeight: 400, 
                            fontSize: "0.75rem" 
                          }}
                        >
                          {showLabel}: {showRank}
                        </span>
                      ) : null;
                    })()
                  )}
                </Box>
                {renderScore(img)}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {visibleCount < results.length ? (
        <Box sx={{ textAlign: "center", mt: 2, mb: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Button
            variant="contained"
            onClick={handleLoadMore}
            sx={{ px: 4, py: 1.2, borderRadius: 2, mt: 1 }}
          >
            Load More
          </Button>
          {(visibleCount >= 20) && image_key && file && hasSearched && (
            <Box sx={{ mt: 1 }}>
              {ReportNotFoundButton()}
            </Box>
          )}
        </Box>
      )
      : image_key && file && hasSearched && (
         <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
          <Box sx={{ mt: 1 }}>
            {ReportNotFoundButton()}
          </Box>
         </Box>
      )}

      {selectedItem && (
        <ProductModal
          open={open}
          onClose={handleClose}
          image_key={image_key}
          sku={selectedItem.sku}
          rank={selectedItem.rank}
          client={selectedItem.client} // pass client here
          reported={reported}
          setReported={setReported}
        />
      )}
    </>
  );
}

const Score = ({ label, value }) =>
  value != null ? (
    <Typography variant="caption">
      <strong>{label}:</strong> {value.toFixed(4)}
    </Typography>
  ) : null;
